import I18n from 'i18n-js';
import Icon from '../generic/Icon.js';
import Button from '../generic/Button.js';
import Avatar from '../generic/Avatar.js';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import React, { Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { getRoomToken } from '../../utils/UrlHelpers.js';
import classNames from 'classnames';

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    '& .title-icon': {
      color: theme.palette.text.secondary,
    },
    '& svg.icon': {
      color: theme.palette.text.secondary,
      fill: theme.palette.text.secondary,
    },
  },
}));

const podiumStyles = makeStyles((theme) => ({
  header: {
    '& .subtitle': {
      color: theme.palette.text.secondary,
    },
  },
}));

const Participant = ({ placeholder, id, avatar, name }) => {
  const className = 'participant' + (placeholder ? ' placeholder' : '');

  return (
    <div className={className} key={id}>
      {name && <Avatar url={avatar} label={name} />}
      {name && <Typography variant="subtitle1">{name}</Typography>}
    </div>
  );
};

// type is "audio", "video" or "presentation"
const Podium = ({ type, icon, participants, onEvent }) => {
  const classes = useStyles();

  if (participants.length === 0) return null;

  return (
    <div className={type}>
      <Typography variant="h6" className={classes.row}>
        <Icon type={icon || type} title={type} className="title-icon" />
        &thinsp;
        {I18n.t(`eco:${type}_participants`)}
        {type === 'presentation' ? (
          <Button
            icon="photo_camera"
            type="secondary"
            onClick={() => onEvent({ type: 'snapshot' })}
          >
            {I18n.t('label:snapshot')}
          </Button>
        ) : (
          <>&thinsp; ({participants.length})</>
        )}
      </Typography>
      <div className="participants">
        {participants.map((p) => (
          <Participant key={p.id} {...p} />
        ))}
      </div>
    </div>
  );
};

/**
 * Podium Outline
 * When users connect to the room in call mode (without sending and receiving
 * video), we provide an outline of the current podium.
 **/
const PodiumOutline = (props) => {
  const classes = podiumStyles();

  const orderedParticipants = () => {
    const { participants, sortOrder, videoSources } = props;
    return participants
      .sort((a, b) => {
        if (!sortOrder || sortOrder.length === 0) {
          return a.id < b.id ? -1 : 1;
        }
        const orderA = sortOrder.indexOf(a.id);
        const orderB = sortOrder.indexOf(b.id);
        if (orderA === -1 && orderB === -1) {
          return a.id < b.id ? -1 : 1;
        }
        if (orderA === -1) {
          return 1;
        }
        if (orderB === -1) {
          return -1;
        }
        return orderA - orderB;
      })
      .map((participant) => {
        const type = videoSources.includes(participant.id) ? 'video' : 'audio';
        return Object.assign(participant, { type: type });
      });
  };

  const handleClick = () =>
    (window.location.href = `${window.location.origin}?${getRoomToken()}`);

  if (!props.visible) {
    return null;
  }

  const {
    title = I18n.t('eco:title'),
    subtitle = I18n.t('eco:subtitle'),
    onEvent,
    active: { hasPresenter, presenter },
    withDisableOption,
  } = props;
  const participants = orderedParticipants();
  const videoPodium = participants.filter((p) => p.type === 'video');
  const audioPodium = participants.filter((p) => p.type === 'audio');
  const apiPresenter = participants.filter((p) => p.id === presenter);

  return (
    <div className="podium-outline">
      <div className={classNames('header', classes.header)}>
        <div className="title">
          <Typography variant="h5">{title}</Typography>
          <Typography variant="subtitle1" className="subtitle">
            {subtitle}
          </Typography>
        </div>
        {withDisableOption && (
          <div className="action">
            <Button onClick={handleClick} icon="videocam">
              {I18n.t('eco:leave:title')}
            </Button>
          </div>
        )}
      </div>
      <div className="podium-frame">
        <div className="podium">
          {hasPresenter ? (
            <Fragment>
              <Podium
                type="presentation"
                participants={apiPresenter}
                onEvent={onEvent}
              />
              <Podium type="audio" icon="mic" participants={audioPodium} />
            </Fragment>
          ) : (
            <Fragment>
              <Podium type="video" icon="videocam" participants={videoPodium} />
              <Podium type="audio" icon="mic" participants={audioPodium} />
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

PodiumOutline.propTypes = {
  visible: PropTypes.bool,
  sortOrder: PropTypes.array.isRequired,
  participants: PropTypes.array.isRequired,
};

PodiumOutline.defaultProps = {
  visible: true,
  withDisableOption: true,
};

export default PodiumOutline;
